// LIDER-IA Enterprise Diagnostic Framework
// 5 Dimensions: L(iderazgo), I(nnovación), D(atos), E(tica), R(oles)

export interface SubDimensionRubric {
  level: number;
  state: string;
  criteria: string;
}

export interface DiagnosticSubSection {
  id: string;
  title: string;
  questions: {
    id: string;
    question: string;
  }[];
  rubrics: SubDimensionRubric[];
}

export interface DiagnosticDimension {
  id: string;
  letter: string;
  name: string;
  fullName: string;
  description: string;
  color: string;
  subSections: DiagnosticSubSection[];
  maturityLevels: Record<number, string>;
}

export interface EnterpriseDiagnosticAnswer {
  text: string;
  cannotAnswer: boolean;
}

export type DimensionId = 'L' | 'I' | 'D' | 'E' | 'R';

export interface SubDimensionScore {
  subDimensionId: string;
  subDimensionName: string;
  score: number; // 1-5
  justification: string;
}

export interface DimensionScore {
  dimensionId: DimensionId;
  name: string;
  score: number; // 1-5
  answeredQuestions: number;
  totalQuestions: number;
  subDimensionScores?: SubDimensionScore[];
}

export interface ImbalanceAlert {
  highDimension: DimensionId;
  highScore: number;
  lowDimension: DimensionId;
  lowScore: number;
  gap: number;
  message: string;
}

export interface EnterpriseDiagnosticResult {
  overallLevel: number; // 1-5
  overallLevelName: string;
  dimensionScores: DimensionScore[];
  totalAnswered: number;
  totalQuestions: number;
  completionPercentage: number;
  imbalanceAlerts: ImbalanceAlert[];
}

export const maturityLevelNames: Record<number, { name: string; description: string }> = {
  1: { name: 'Inicial (Ad-hoc)', description: 'Uso accidental, riesgos no gestionados, silos de información y "Shadow AI".' },
  2: { name: 'Emergente', description: 'Experimentación activa, creación de los primeros centros de competencia y conciencia regulatoria.' },
  3: { name: 'Estandarizado', description: 'Procesos definidos, metodologías claras (MLOps, Crisp-DM) y operatividad recurrente.' },
  4: { name: 'Gobernado', description: 'Alineación estratégica total, cumplimiento de estándares internacionales (ISO 42001) y medición de ROI.' },
  5: { name: 'Visionario', description: 'IA como motor de transformación del modelo de negocio, innovación continua y auditoría automatizada.' },
};

export const dimensions: DiagnosticDimension[] = [
  {
    id: 'L',
    letter: 'L',
    name: 'Liderazgo',
    fullName: 'Liderazgo y Estrategia',
    description: 'Mide la visión, la alineación corporativa y la capacidad de la dirección para guiar la transformación.',
    color: 'hsl(var(--primary))',
    subSections: [
      {
        id: 'L1',
        title: 'Visión y Objetivos',
        questions: [
          { id: 'L1.1', question: '¿Cuál es el "Norte" estratégico o propósito superior definido para la IA en la organización?' },
          { id: 'L1.2', question: '¿Cómo se asegura que la IA resuelva problemas críticos del negocio en lugar de ser una adopción por tendencia o moda?' },
          { id: 'L1.3', question: '¿De qué manera la IA está impulsando una transformación profunda del modelo de negocio actual?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'No existe una visión formal. La adopción es reactiva, impulsada por la curiosidad individual o la presión del mercado ("Shadow AI").' },
          { level: 2, state: 'Emergente', criteria: 'Existe un borrador de visión. El liderazgo apoya pilotos aislados para "probar la tecnología", pero no hay conexión con los dolores reales del negocio.' },
          { level: 3, state: 'Estandarizado', criteria: 'El "Norte" estratégico está definido y comunicado. La IA se utiliza para resolver ineficiencias operativas conocidas en procesos de soporte. Hay definida una North Star Metric de IA.' },
          { level: 4, state: 'Gobernado', criteria: 'La IA es un habilitador clave del negocio. Existe un proceso de selección de proyectos basado en el impacto estratégico y la mejora de la experiencia del cliente.' },
          { level: 5, state: 'Visionario', criteria: 'La IA redefine el propósito de la organización. Se busca explícitamente la creación de nuevas líneas de negocio o la transformación radical del modelo operativo.' },
        ],
      },
      {
        id: 'L2',
        title: 'Principios Rectores',
        questions: [
          { id: 'L2.1', question: '¿Qué mecanismos garantizan que los modelos sean honestos y transparentes?' },
          { id: 'L2.2', question: '¿Cómo se asegura la explicabilidad de los modelos (entender el porqué de sus decisiones)?' },
          { id: 'L2.3', question: '¿Qué protocolos existen para garantizar la supervisión humana efectiva en las decisiones automatizadas?' },
          { id: 'L2.4', question: '¿Cómo se garantiza que la utilidad práctica y el valor real primen sobre la novedad tecnológica?' },
          { id: 'L2.5', question: '¿Qué estándares de equidad se aplican para evitar la discriminación algorítmica?' },
          { id: 'L2.6', question: '¿Cómo se integran la Seguridad y la Privacidad no solo como controles técnicos, sino como principios estratégicos de \'Privacidad desde el Diseño\' en cada iniciativa de IA?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Ausencia de principios: No existen definiciones formales de ética, transparencia o equidad. Los modelos operan como "cajas negras" sin mecanismos de explicabilidad.' },
          { level: 2, state: 'Emergente', criteria: 'Declaraciones de intención: Existen lineamientos informales sobre honestidad y transparencia. Hay conciencia de la necesidad de supervisión humana y privacidad, pero se gestionan de forma reactiva.' },
          { level: 3, state: 'Estandarizado', criteria: 'Principios documentados: Definición formal de los principios (Ética, Transparencia, Seguridad). Existen protocolos de Human-in-the-loop para procesos críticos. Se exige explicabilidad técnica básica.' },
          { level: 4, state: 'Gobernado', criteria: 'Integración estratégica y monitoreo: Aplicación sistémica de "Privacidad y Seguridad desde el Diseño". Estándares formales de equidad para detectar y mitigar sesgos algorítmicos. Filtro de "Utilidad sobre Novedad" obligatorio.' },
          { level: 5, state: 'Visionario', criteria: 'Cultura de Transformación Ética: Los principios son una ventaja competitiva de marca. La explicabilidad (XAI) es comprensible para el usuario final. Auditoría ética automatizada en tiempo real.' },
        ],
      },
      {
        id: 'L3',
        title: 'Contexto, Políticas y Alineación',
        questions: [
          { id: 'L3.1', question: '¿Se han identificado los factores PESTEL (Políticos, Económicos, Sociales, Tecnológicos, Ecológicos y Legales) específicos que afectan la IA?' },
          { id: 'L3.2', question: '¿Quiénes son los stakeholders clave (internos y externos) y cómo se gestionan sus expectativas?' },
          { id: 'L3.3', question: '¿Existe un ejercicio de benchmarking para comparar el avance en IA frente a competidores del sector?' },
          { id: 'L3.4', question: '¿Existe un marco normativo interno (políticas) que regule el desarrollo y uso de la IA?' },
          { id: 'L3.5', question: '¿Cómo se garantiza la alineación total entre la hoja de ruta de IA y el Plan Estratégico Corporativo?' },
          { id: 'L3.6', question: '¿Cómo define la organización su "Apetito de Riesgo" específico para la IA? En escenarios de conflicto, ¿qué criterio prevalece: la velocidad de innovación (time-to-market) o la exhaustividad de los controles de seguridad y sesgo?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Desconocimiento de normativas (SIC, AI Act). No hay políticas internas. IA y Plan Estratégico van por caminos separados.' },
          { level: 2, state: 'Emergente', criteria: 'Mapeo inicial de stakeholders. Conciencia de la Circular 002 de la SIC. Identificación básica de riesgos legales externos.' },
          { level: 3, state: 'Estandarizado', criteria: 'Políticas internas de uso aceptable documentadas. Benchmarking sectorial activo. Alineación con el Roadmap de TI.' },
          { level: 4, state: 'Gobernado', criteria: 'Implementación de ISO 42001. Alineación total con los OKRs/KPIs corporativos. Apetito de riesgo definido por la Junta Directiva.' },
          { level: 5, state: 'Visionario', criteria: 'Cumplimiento proactivo total. Las políticas evolucionan dinámicamente con la tecnología. Los líderes asumen responsabilidad algorítmica total.' },
        ],
      },
      {
        id: 'L4',
        title: 'Gobierno y ROI',
        questions: [
          { id: 'L4.1', question: '¿Cuál es el esquema operativo elegido: Centralizado, Federado o Descentralizado?' },
          { id: 'L4.2', question: '¿Cómo funciona el proceso de Hoja de Ruta IA para priorizar proyectos habilitadores?' },
          { id: 'L4.3', question: '¿Cuáles son los KPIs específicos para medir el éxito de la IA?' },
          { id: 'L4.4', question: '¿Qué modelo se utiliza para calcular y validar el Retorno de Inversión (ROI)?' },
          { id: 'L4.5', question: '¿Con qué frecuencia y mediante qué métricas (KRIs - Key Risk Indicators) recibe el Consejo de Administración informes sobre incidentes o fallos de los sistemas de IA?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Caos operativo. No hay presupuesto asignado ni métricas de éxito. Las iniciativas son "proyectos científicos" sin fin claro.' },
          { level: 2, state: 'Emergente', criteria: 'Comité de IA incipiente. Presupuesto para pilotos aprobado. Estimación de ROI basada en supuestos teóricos, no validados.' },
          { level: 3, state: 'Estandarizado', criteria: 'Centro de Excelencia (CoE) centralizado. Hoja de ruta priorizada. Seguimiento de KPIs de eficiencia operativa (ahorro de tiempo/costo).' },
          { level: 4, state: 'Gobernado', criteria: 'Modelo de Gobierno Federado activo. Test de ponderación formal para priorizar proyectos. El Consejo recibe informes de KRIs (riesgos).' },
          { level: 5, state: 'Visionario', criteria: 'El ROI se mide en tiempo real. La IA impacta directamente en el crecimiento del P&L.' },
        ],
      },
    ],
    maturityLevels: {
      1: 'No existe una estrategia de IA. La adopción es por tendencia.',
      2: 'Se define un "Norte" estratégico. El liderazgo apoya pilotos aislados sin KPIs claros de negocio.',
      3: 'Existe una Hoja de Ruta (Roadmap) priorizada. El Consejo de Administración comienza a recibir informes básicos.',
      4: 'Alineación total con el Plan Estratégico Corporativo. Se utiliza un modelo formal para calcular el ROI.',
      5: 'La IA redefine el propósito de la organización. El liderazgo equilibra perfectamente la velocidad de innovación con el apetito de riesgo.',
    },
  },
  {
    id: 'I',
    letter: 'I',
    name: 'Innovación',
    fullName: 'Innovación',
    description: 'Evalúa la capacidad de descubrir oportunidades, rediseñar procesos y experimentar de forma segura.',
    color: 'hsl(var(--accent))',
    subSections: [
      {
        id: 'I1',
        title: 'Identificación de Oportunidades y Rediseño',
        questions: [
          { id: 'I1.1', question: '¿Cómo se utiliza el Value Stream Mapping u otra metodología relacionada, para encontrar oportunidades de IA en la cadena de valor y procesos de la empresa?' },
          { id: 'I1.2', question: '¿Qué metodologías de diseño centrado en el humano se aplican para asegurar que las soluciones de IA resuelvan necesidades reales del usuario?' },
          { id: 'I1.3', question: '¿Cómo se utiliza la Minería de Procesos (Process Mining) u otra metodología de análisis de datos para detectar cuellos de botella, antes de aplicar IA?' },
          { id: 'I1.4', question: '¿De qué forma se integran marcos de optimización de procesos y eficiencia operativa (ej. Lean Six Sigma) para asegurar la calidad antes de la automatización?' },
          { id: 'I1.5', question: '¿Existe un protocolo para repensar y rediseñar los procesos antes de automatizarlos para evitar "automatizar el caos"?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Adopción por Hype: No existen metodologías para detectar oportunidades. Se automatizan procesos tal como están, lo que resulta en "automatizar el caos".' },
          { level: 2, state: 'Emergente', criteria: 'Ideación Básica: Se realizan sesiones de lluvia de ideas ocasionales. Se identifican "dolores" obvios pero no hay un mapeo de valor ni intención de rediseñar el proceso.' },
          { level: 3, state: 'Estandarizado', criteria: 'Metodologías Activas: Uso regular de Design Thinking o Lean para identificar casos de uso. Existe un análisis previo del proceso y simplificaciones básicas antes de la implementación.' },
          { level: 4, state: 'Gobernado', criteria: 'Optimización Sistémica: Uso de Process Mining o Value Stream Mapping basado en datos reales. El rediseño del proceso es un requisito obligatorio (gate) antes de aprobar cualquier proyecto de IA.' },
          { level: 5, state: 'Visionario', criteria: 'Reinvención del Modelo: La organización repiensa sus procesos desde una lógica "AI-Native". Se borran paradigmas operativos tradicionales para crear flujos hiper-eficientes.' },
        ],
      },
      {
        id: 'I2',
        title: 'Sandbox e Infraestructura de Innovación',
        questions: [
          { id: 'I2.1', question: '¿Existe un Sandbox IA (entorno seguro) para experimentar y "fallar rápido y barato"?' },
          { id: 'I2.2', question: '¿Cuentan con una Ruta de Retos o Demodays para incentivar el prototipado continuo? ¿Qué mecanismos de incentivos a la experimentación y exposición de prototipos existen para fomentar la innovación continua?' },
          { id: 'I2.3', question: '¿Se cuenta con mentores internos de IA que asesoren a las áreas de negocio?' },
          { id: 'I2.4', question: '¿Permite el Sandbox Tecnológico el uso seguro de datos ficticios o anonimizados para pruebas?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Inexistente: No hay entorno de pruebas. Los empleados usan herramientas públicas con datos reales de la empresa sin autorización ni control.' },
          { level: 2, state: 'Emergente', criteria: 'Experimentación Ad-hoc: Se permiten pruebas en cuentas "trial" aisladas. No hay mentores internos y la infraestructura tecnológica para innovar es limitada.' },
          { level: 3, state: 'Estandarizado', criteria: 'Sandbox Básico: Disponibilidad de herramientas aprobadas (ej: Copilot Studio, n8n). Existe una ruta de retos interno. Hay figuras identificadas que guían el conocimiento técnico inicial.' },
          { level: 4, state: 'Gobernado', criteria: 'Ecosistema de Innovación: Sandbox avanzado con uso de datos sintéticos y acceso seguro a APIs. Ruta de Demodays recurrente. Células de innovación activas que validan prototipos frente a principios éticos.' },
          { level: 5, state: 'Visionario', criteria: 'Agilidad Agéntica: Cultura de "fallar rápido y barato" totalmente operativa. Uso de vibecoding para prototipado ultrarrápido. Privacidad total por diseño y democratización masiva de la innovación.' },
        ],
      },
      {
        id: 'I3',
        title: 'Portafolio y Propiedad Intelectual',
        questions: [
          { id: 'I3.1', question: '¿Cómo se gestiona el portafolio de proyectos según su riesgo, impacto y tiempo de ejecución?' },
          { id: 'I3.2', question: '¿Qué medidas se toman para proteger la propiedad intelectual de los algoritmos y datos generados in-house?' },
          { id: 'I3.3', question: '¿Cuál es el criterio para decidir entre desarrollar internamente o comprar soluciones comerciales?' },
          { id: 'I3.4', question: '¿El modelo de evaluación de proyectos incluye los costos ocultos de inferencia, licencias dinámicas y mantenimiento de MLOps, o solo el costo de desarrollo inicial?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Gestión Ciega: No hay inventario de proyectos de IA. No existe preocupación por la propiedad intelectual. La compra de tecnología es reactiva y sin criterios técnicos.' },
          { level: 2, state: 'Emergente', criteria: 'Listado de Pilotos: Existe una lista de iniciativas activas. Se tiene conciencia de que la IP es importante pero no se han registrado marcas o derechos de autor.' },
          { level: 3, state: 'Estandarizado', criteria: 'Portafolio Estructurado: Los proyectos se clasifican por impacto y complejidad. Existen criterios básicos de "Build vs Buy" dictados por TI. Registro formal de software propio.' },
          { level: 4, state: 'Gobernado', criteria: 'Gestión Integral de Activos: Evaluación del portafolio bajo el modelo completo (ROI, Ética, Datos, MLOps). Protección proactiva de IP sobre prompts complejos y algoritmos. Se desarrolla internamente solo lo que es Core Business.' },
          { level: 5, state: 'Visionario', criteria: 'Estrategia de Capital Intelectual: La IP de IA es vista como una ventaja competitiva protegida globalmente. El portafolio se optimiza dinámicamente según el mercado.' },
        ],
      },
    ],
    maturityLevels: {
      1: 'Innovación caótica. Se automatizan procesos ineficientes ("automatizar el caos").',
      2: 'Se crean los primeros prototipos. Existe un entorno de prueba (Sandbox) básico.',
      3: 'Uso de Process Mining o Lean antes de aplicar IA. Existe una ruta de retos internos.',
      4: 'Diseño centrado en el humano como estándar. El portafolio de proyectos se gestiona por riesgo e impacto.',
      5: 'Cultura de "fallar rápido y barato". Sandbox avanzado con datos sintéticos. Protección proactiva de la Propiedad Intelectual.',
    },
  },
  {
    id: 'D',
    letter: 'D',
    name: 'Datos',
    fullName: 'Datos y Tecnología',
    description: 'Analiza la infraestructura, la operacionalización (Ops) y la robustez técnica.',
    color: 'hsl(var(--warning))',
    subSections: [
      {
        id: 'D1',
        title: 'Inventarios y Arquitectura',
        questions: [
          { id: 'D1.1', question: '¿Existe un inventario detallado de todas las herramientas, modelos, fuentes de datos y aplicaciones de IA en uso? Explique en detalle qué inventarios sí tiene y cuáles no.' },
          { id: 'D1.2', question: '¿Cómo se garantiza la calidad y el linaje de los datos para que sean confiables y trazables?' },
          { id: 'D1.3', question: '¿Cómo se integran los datos a través de pipelines (ETL), Data Warehouses o Data Lakes?' },
          { id: 'D1.4', question: '¿Es la infraestructura TI (Cloud/Híbrida) capaz de escalar según las demandas de procesamiento de la IA?' },
          { id: 'D1.5', question: '¿Cuenta la arquitectura actual con bases de datos vectoriales y procesos de sincronización en tiempo real para asegurar que la IA no trabaje con información obsoleta?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Caos de Activos: No existen inventarios de modelos ni herramientas. La infraestructura es obsoleta o rígida. El linaje del dato es desconocido y la calidad es pobre.' },
          { level: 2, state: 'Emergente', criteria: 'Registro Manual: Existen listados básicos (Excel) de algunas herramientas y fuentes de datos. Pipelines de datos manuales y fragmentados. No hay bases de datos vectoriales.' },
          { level: 3, state: 'Estandarizado', criteria: 'Arquitectura Integrada: Inventario centralizado de herramientas y modelos. Infraestructura en la nube con escalabilidad básica. Criterios de calidad de datos y linaje en procesos clave.' },
          { level: 4, state: 'Gobernado', criteria: 'Ecosistema Robusto: Repositorio de soluciones IA consolidado para reutilización. Uso de bases de datos vectoriales y grafos. Trazabilidad total (procedencia) exigida por la SIC integrada en el flujo técnico.' },
          { level: 5, state: 'Visionario', criteria: 'Infraestructura Agéntica: Arquitectura diseñada para agentes autónomos. Integración en tiempo real (ETL/Streaming) optimizada. Linaje automatizado y autosanación de datos.' },
        ],
      },
      {
        id: 'D2',
        title: 'Operacionalización (Ops) y Seguridad Técnica',
        questions: [
          { id: 'D2.1', question: '¿Cómo se gestiona el ciclo de vida técnico mediante MLOps o LLMOps?' },
          { id: 'D2.2', question: '¿Existen pipelines de CI/CD que permitan el versionado y rollback automático de modelos si se detecta una degradación en la calidad de las respuestas en producción?' },
          { id: 'D2.3', question: '¿Qué herramientas de observabilidad monitorizan el desempeño del modelo en tiempo real?' },
          { id: 'D2.4', question: '¿Cómo se detecta y controla el uso de Shadow AI (herramientas de IA no autorizadas)?' },
          { id: 'D2.5', question: '¿Existen protocolos de ciberseguridad específicos para proteger los sistemas de IA?' },
          { id: 'D2.6', question: '¿Se realizan ejercicios de pruebas de seguridad adversarias (Red Teaming) para evaluar la resistencia ante ataques?' },
          { id: 'D2.7', question: '¿Qué criterios de sostenibilidad ambiental y eficiencia de cómputo se consideran en la arquitectura de IA?' },
          { id: 'D2.8', question: '¿Qué mecanismos de "Circuit Breakers" (parada automática) existen para detener agentes o modelos que entren en bucles de consumo infinito de tokens o recursos de cómputo?' },
          { id: 'D2.9', question: '¿Cómo se evalúa si una aplicación heredada (monolítica) debe ser refactorizada antes de intentar integrarle capacidades de IA para evitar "radios de explosión" en caso de error?' },
          { id: 'D2.10', question: '¿En qué medida los sistemas críticos permiten la interacción programática (APIs) para que la IA acceda a la lógica de negocio sin depender de la interfaz de usuario?' },
          { id: 'D2.11', question: '¿Más allá de los errores técnicos (404, 500), los sistemas de logging capturan el contexto de la interacción (qué se preguntó y qué se respondió) para realizar análisis forenses?' },
          { id: 'D2.12', question: '¿Qué defensas de infraestructura (WAF, API Gateways) están configuradas específicamente para bloquear ataques de "Prompt Injection" o intentos de "Jailbreak" antes de que lleguen al modelo?' },
          { id: 'D2.13', question: '¿Qué herramientas de monitoreo de red o endpoint se utilizan para identificar y bloquear la exfiltración de datos hacia herramientas de IA externas no autorizadas por la empresa?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Vulnerabilidad: Despliegue manual de modelos. Shadow AI fuera de control. No hay monitoreo de alucinaciones ni pruebas de seguridad específicas para IA.' },
          { level: 2, state: 'Emergente', criteria: 'Conciencia de Riesgo: Protocolos iniciales para detectar herramientas no autorizadas. Primeros experimentos de MLOps. Monitoreo básico del rendimiento del modelo (uptime).' },
          { level: 3, state: 'Estandarizado', criteria: 'Operación Controlada: Implementación de MLOps/LLMOps básico (CI/CD). Control activo de Shadow AI. Pruebas de seguridad (Red Teaming) periódicas.' },
          { level: 4, state: 'Gobernado', criteria: 'Observabilidad Avanzada: Monitoreo en tiempo real de sesgos, drift y alucinaciones. Estrategia de Rainbow Teaming activa. Implementación de métricas de Green AI para eficiencia energética.' },
          { level: 5, state: 'Visionario', criteria: 'Resiliencia Autónoma: Mecanismos de "Circuit Breakers" automáticos ante fallos críticos. Ciclo de vida de agentes autónomos totalmente automatizado. Excelencia en sostenibilidad y seguridad ofensiva.' },
        ],
      },
      {
        id: 'D3',
        title: 'Metodologías',
        questions: [
          { id: 'D3.1', question: '¿Se aplican marcos de análisis de datos como Crisp-DM, IBM Enterprise Workflow o TDSP?' },
          { id: 'D3.2', question: '¿Cómo se gestiona el ciclo de vida de los agentes autónomos (AELC)?' },
          { id: 'D3.3', question: '¿Qué metodología se sigue para la automatización inteligente (IALC)?' },
          { id: 'D3.4', question: '¿Se utilizan metodologías para el desarrollo de aplicaciones por Vibecoding / VDD?' },
          { id: 'D3.5', question: '¿Cómo se regula el ciclo de vida del contenido generado por IA (ACL)?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Sin Método: El desarrollo depende del "héroe" de turno. No hay estándares para crear agentes, contenidos o aplicaciones. Resultados inconsistentes y no escalables.' },
          { level: 2, state: 'Emergente', criteria: 'Métodos Aislados: Se definen pasos para proyectos específicos, pero no son transversales. Conciencia de la necesidad de metodologías.' },
          { level: 3, state: 'Estandarizado', criteria: 'Metodologías Base: Implementación de SDD para aplicaciones y ACL para contenidos en equipos clave. El flujo de trabajo de IA empresarial está documentado.' },
          { level: 4, state: 'Gobernado', criteria: 'Rigor Metodológico: Uso sistémico de AELC (Ciclo de Agentes) e IALC (Automatización Inteligente). Cada iniciativa debe seguir el método estándar para ser aprobada por el CoE.' },
          { level: 5, state: 'Visionario', criteria: 'Maestría Metodológica: Evolución continua de los marcos de trabajo. Uso masivo de vibecoding y SDD para prototipado ultrarrápido. Las metodologías son un activo de IP.' },
        ],
      },
    ],
    maturityLevels: {
      1: 'Infraestructura obsoleta. No hay inventario de modelos ni control de Shadow AI.',
      2: 'Inventarios iniciales de herramientas. Pipelines de datos manuales.',
      3: 'MLOps/LLMOps básico implementado. CI/CD para modelos y escalabilidad en la nube.',
      4: 'Arquitectura con bases de datos vectoriales. Monitoreo en tiempo real y seguridad ofensiva (Red Teaming).',
      5: 'Ciclo de vida de agentes autónomos definido. Mecanismos de "Circuit Breakers" automáticos y criterios de sostenibilidad ambiental.',
    },
  },
  {
    id: 'E',
    letter: 'E',
    name: 'Ética',
    fullName: 'Ética y Gobernanza',
    description: 'Mide la gestión de riesgos, el cumplimiento normativo y la transparencia algorítmica.',
    color: 'hsl(var(--success))',
    subSections: [
      {
        id: 'E1',
        title: 'Gestión de Riesgos',
        questions: [
          { id: 'E1.1', question: '¿Cómo se gestiona el riesgo de los proyectos con IA?' },
          { id: 'E1.2', question: '¿Qué controles existen para detectar y reducir las alucinaciones en modelos de IA generativa?' },
          { id: 'E1.3', question: '¿Cuáles son los riesgos de seguridad específicos de la IA (ej. inyección de prompts) que han sido mapeados?' },
          { id: 'E1.4', question: '¿Cómo se evalúa el riesgo de pérdida de privacidad de los datos en los modelos de entrenamiento?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Reactivo: No se identifican sesgos ni alucinaciones antes del despliegue. Los riesgos se abordan solo cuando ocurre un fallo público o legal.' },
          { level: 2, state: 'Emergente', criteria: 'Mapeo Básico: Existe una lista informal de riesgos potenciales (ej: filtración de datos). Se discuten los sesgos pero no hay una metodología para medirlos.' },
          { level: 3, state: 'Estandarizado', criteria: 'Marco Formal: Aplicación de una metodología de riesgos (ej. ISO 31000 o NIST). Se documentan riesgos específicos de IA como ataques adversarios en proyectos clave.' },
          { level: 4, state: 'Gobernado', criteria: 'Gestión Preventiva: Matriz de riesgos de IA viva e integrada en el ERM corporativo. Evaluación obligatoria de riesgos éticos antes de cada paso a producción.' },
          { level: 5, state: 'Visionario', criteria: 'Resiliencia Adaptativa: Monitoreo automatizado de riesgos en tiempo real. La organización anticipa riesgos de modelos frontera y lidera estándares éticos en su sector.' },
        ],
      },
      {
        id: 'E2',
        title: 'Cumplimiento y Ciclo de Vida',
        questions: [
          { id: 'E2.1', question: '¿Cómo se gestiona el ciclo de vida completo del modelo, desde la concepción hasta el retiro (decommissioning)?' },
          { id: 'E2.2', question: '¿Se realiza una evaluación formal de impacto en la protección de datos (DPIA) y en los derechos fundamentales antes de autorizar el paso a producción de una solución IA?' },
          { id: 'E2.3', question: '¿Cómo se asegura el cumplimiento regulatorio (ej. circular externa 002 de 2024 de la SIC y Ley 1581 de 2012) y la alineación con leyes internacionales como el AI Act?' },
          { id: 'E2.4', question: '¿De qué manera se aplica la Privacidad desde el Diseño (Privacy by Design)?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Incumplimiento: Desconocimiento de la Circular 002/003 de la SIC. No hay control sobre el ciclo de vida. Los datos se usan sin verificar veracidad o derechos.' },
          { level: 2, state: 'Emergente', criteria: 'Conciencia Normativa: Intención de cumplir la Ley 1581. Se han leído las circulares de la SIC pero no se aplican Evaluaciones de Impacto (DPIA) de forma técnica.' },
          { level: 3, state: 'Estandarizado', criteria: 'Gobernanza de Datos: Implementación de DPIAs en procesos críticos. Avisos de privacidad que mencionan IA. El ciclo de vida tiene etapas de revisión definidas.' },
          { level: 4, state: 'Gobernado', criteria: 'Responsabilidad Demostrada: Aplicación sistémica de "Privacidad desde el Diseño". Pruebas de veracidad y técnicas de privacidad diferencial integradas. Cumplimiento verificado de la Circular 003 por la Junta.' },
          { level: 5, state: 'Visionario', criteria: 'Ética por Defecto: El ciclo de vida está totalmente automatizado con controles de cumplimiento (Policy-as-Code). El respeto a los derechos fundamentales es el eje del diseño agéntico. Alineación con el EU AI Act.' },
        ],
      },
      {
        id: 'E3',
        title: 'Auditoría e Incidentes',
        questions: [
          { id: 'E3.1', question: '¿Existe documentación que permita la trazabilidad total para auditorías internas o externas para procesos y proyectos asociados a la IA?' },
          { id: 'E3.2', question: '¿Cuál es el protocolo de respuesta ante un incidente o fallo crítico de un sistema de IA?' },
          { id: 'E3.3', question: '¿Cómo se evalúa la ética y la seguridad de los proveedores terceros de herramientas de IA (ej. N8n, Copilot Studio,...) y modelos de IA (ej. OpenAI, Anthropic, Google…)?' },
          { id: 'E3.4', question: '¿Cómo se asegura que las explicaciones de los modelos (XAI) sean comprensibles para el usuario final afectado (ej: un cliente rechazado) y no solo para los auditores?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Opacidad: No hay registros de auditoría ni documentación técnica. Ante un incidente, no hay un plan de acción. Los proveedores se contratan sin revisión ética.' },
          { level: 2, state: 'Emergente', criteria: 'Registro Manual: Documentación técnica básica de algunos modelos. Plan de respuesta a incidentes de TI general, no específico para IA.' },
          { level: 3, state: 'Estandarizado', criteria: 'Trazabilidad: Existencia de "Model Cards" y logs de decisiones. Protocolo de notificación de incidentes definido. Evaluación básica de seguridad de proveedores.' },
          { level: 4, state: 'Gobernado', criteria: 'Explicabilidad (XAI) Funcional: El sistema puede explicar técnicamente el porqué de un resultado. Evaluación ética rigurosa de proveedores. Protocolos de respuesta con roles claros.' },
          { level: 5, state: 'Visionario', criteria: 'Auditoría Continua: Explicabilidad (XAI) comprensible para el ciudadano común. Gestión de incidentes con "Circuit Breakers" automáticos. Auditoría externa periódica y automatizada.' },
        ],
      },
    ],
    maturityLevels: {
      1: 'Alto riesgo legal. No hay controles para alucinaciones o sesgos.',
      2: 'Mapeo de riesgos inicial. Conciencia de normativas (Ley 1581, Circular 002 SIC).',
      3: 'Evaluaciones de Impacto (PIAs) recurrentes. Documentación trazable para auditoría humana.',
      4: 'Gobernanza integrada (ISO 42001). Evaluación ética de proveedores terceros. Explicabilidad técnica (XAI).',
      5: 'Auditoría automatizada. La ética es una ventaja competitiva. Explicabilidad comprensible para el usuario final afectado.',
    },
  },
  {
    id: 'R',
    letter: 'R',
    name: 'Roles',
    fullName: 'Roles y Talento',
    description: 'Evalúa la estructura organizacional, la capacitación y la cultura de colaboración humano-IA.',
    color: 'hsl(var(--destructive))',
    subSections: [
      {
        id: 'R1',
        title: 'Estructura y Responsabilidad Algorítmica',
        questions: [
          { id: 'R1.1', question: '¿Están definidos y contratados roles como CAIO (Chief AI Officer), Ingenieros de Datos, Científicos de Datos e IA Product Manager, entre otros?' },
          { id: 'R1.2', question: '¿Cómo opera el Centro de Excelencia (CoE) y cómo centraliza el conocimiento?' },
          { id: 'R1.3', question: '¿Se han actualizado los manuales de funciones para reflejar la colaboración con la IA?' },
          { id: 'R1.4', question: '¿Cómo se garantiza que el líder de negocio asuma la Responsabilidad Algorítmica (dueño del resultado)?' },
          { id: 'R1.5', question: '¿Cómo se operacionaliza la Supervisión Humana en el día a día?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Silos y Ausencia de Roles: No hay roles específicos para IA. La responsabilidad de los fallos técnicos se delega únicamente a TI. Los manuales de funciones son obsoletos.' },
          { level: 2, state: 'Emergente', criteria: 'CoE Incipiente: Existe un comité de IA informal o en formación. Se han identificado los perfiles necesarios (ej: CAIO, Ingenieros) pero aún no han sido contratados.' },
          { level: 3, state: 'Estandarizado', criteria: 'Estructura Operativa: Centro de Excelencia (CoE) centralizado y activo. Roles clave como IA Product Manager y Científicos de Datos están contratados. Primeros ajustes en manuales de funciones.' },
          { level: 4, state: 'Gobernado', criteria: 'Responsabilidad Federada: Los líderes de negocio asumen la Responsabilidad Algorítmica. El CoE opera bajo un modelo Hub & Spoke. Los manuales reflejan el cambio del "hacer" al "pensar y conectar".' },
          { level: 5, state: 'Visionario', criteria: 'Maestría Agéntica: La IA está embebida en la identidad organizacional. La supervisión humana estratégica es parte de la cultura diaria. Organigrama fluido con células de innovación autónomas.' },
        ],
      },
      {
        id: 'R2',
        title: 'Alfabetización y Cultura',
        questions: [
          { id: 'R2.1', question: '¿Existe una ruta formal de Alfabetización en IA (AI Literacy) para todos los niveles de la empresa?' },
          { id: 'R2.2', question: '¿Qué estrategias de gestión del cambio se utilizan para evolucionar el mindset organizacional?' },
          { id: 'R2.3', question: '¿Existe una base de conocimiento centralizada que registre éxitos, fallos y lecciones aprendidas?' },
          { id: 'R2.4', question: '¿Cómo se asegura la comunicación constante de los resultados para generar confianza en la IA?' },
        ],
        rubrics: [
          { level: 1, state: 'Inicial', criteria: 'Resistencia Cultural: Existe miedo generalizado al desplazamiento laboral. No hay formación en IA. La información sobre proyectos de IA se maneja de forma cerrada o confidencial.' },
          { level: 2, state: 'Emergente', criteria: 'Sensibilización: Se realizan charlas informativas u ocasionales sobre IA. Existe una intención de capacitar al personal, pero no hay una ruta estructurado de AI Literacy.' },
          { level: 3, state: 'Estandarizado', criteria: 'Alfabetización Básica: Ruta formal de capacitación activo para niveles operativos (ej: Prompt Engineering). Repositorio inicial de lecciones aprendidas y casos de uso exitosos.' },
          { level: 4, state: 'Gobernado', criteria: 'Gestión del Cambio Aplicada: Uso sistémico de metodologías como ADKAR o Kotter. Alfabetización masiva que incluye alta gerencia en ROI y ética. Base de conocimiento compartida y transparente.' },
          { level: 5, state: 'Visionario', criteria: 'Mindset Colaborativo: La IA se percibe como un potenciador de capacidades humanas. La experimentación es recompensada y los fallos se analizan abiertamente. Evolución constante del capital intelectual de forma autogestionada.' },
        ],
      },
    ],
    maturityLevels: {
      1: 'No hay roles definidos. Existe miedo al desplazamiento laboral por la IA.',
      2: 'Creación de un CoE incipiente. Primeros rutas de alfabetización (AI Literacy).',
      3: 'Roles clave contratados (Científicos de datos, IA PM). El CoE centraliza el conocimiento.',
      4: 'Definición de Responsabilidad Algorítmica para líderes de negocio. Alfabetización en todos los niveles.',
      5: 'Evolución total del mindset. Gestión del cambio madura con base de lecciones aprendidas compartida.',
    },
  },
];

// Helper to get all questions flat
export function getAllQuestions(): { dimensionId: string; subSectionId: string; questionId: string; question: string }[] {
  const result: { dimensionId: string; subSectionId: string; questionId: string; question: string }[] = [];
  for (const dim of dimensions) {
    for (const sub of dim.subSections) {
      for (const q of sub.questions) {
        result.push({ dimensionId: dim.id, subSectionId: sub.id, questionId: q.id, question: q.question });
      }
    }
  }
  return result;
}

export function getTotalQuestionCount(): number {
  return dimensions.reduce((acc, dim) => 
    acc + dim.subSections.reduce((a, s) => a + s.questions.length, 0), 0
  );
}

export function getDimensionQuestionCount(dimensionId: string): number {
  const dim = dimensions.find(d => d.id === dimensionId);
  if (!dim) return 0;
  return dim.subSections.reduce((a, s) => a + s.questions.length, 0);
}

// Build rubric context for AI evaluation
// overrides: optional map { "L1": { 1: "custom criteria", 3: "..." }, ... }
export function buildRubricContext(overrides?: Record<string, Record<number, string>>): string {
  return dimensions.map(dim => {
    const subSectionRubrics = dim.subSections.map(sub => {
      const rubricTable = sub.rubrics.map(r => {
        const criteria = overrides?.[sub.id]?.[r.level] ?? r.criteria;
        return `  Nivel ${r.level} (${r.state}): ${criteria}`;
      }).join('\n');
      return `### ${sub.id}: ${sub.title}\nPreguntas: ${sub.questions.map(q => q.id).join(', ')}\n${rubricTable}`;
    }).join('\n\n');
    return `## Dimensión ${dim.letter}: ${dim.fullName}\n${subSectionRubrics}`;
  }).join('\n\n---\n\n');
}

// Detect imbalance alerts (gap >= 2 levels between any two dimensions)
export function detectImbalances(dimensionScores: DimensionScore[]): ImbalanceAlert[] {
  const alerts: ImbalanceAlert[] = [];
  const criticalPairs: [DimensionId, DimensionId, string][] = [
    ['D', 'E', 'La fortaleza técnica no compensa la debilidad ética. Una organización con alto nivel en Datos/Tecnología pero bajo en Ética posee un "pasivo de riesgo" que pone en peligro su continuidad.'],
    ['I', 'E', 'Innovar sin gobernanza ética genera riesgos legales y reputacionales significativos.'],
    ['L', 'R', 'Una estrategia ambiciosa sin el talento y la cultura adecuados no podrá ejecutarse.'],
    ['D', 'R', 'La infraestructura tecnológica avanzada sin personal capacitado para gobernarla genera dependencia y riesgo.'],
  ];

  for (const [high, low, message] of criticalPairs) {
    const highScore = dimensionScores.find(d => d.dimensionId === high);
    const lowScore = dimensionScores.find(d => d.dimensionId === low);
    if (highScore && lowScore) {
      const gap = highScore.score - lowScore.score;
      if (gap >= 2) {
        alerts.push({
          highDimension: high,
          highScore: highScore.score,
          lowDimension: low,
          lowScore: lowScore.score,
          gap,
          message,
        });
      }
      // Check reverse
      const reverseGap = lowScore.score - highScore.score;
      if (reverseGap >= 2) {
        alerts.push({
          highDimension: low,
          highScore: lowScore.score,
          lowDimension: high,
          lowScore: highScore.score,
          gap: reverseGap,
          message,
        });
      }
    }
  }

  // Also check any pair with gap >= 3
  for (let i = 0; i < dimensionScores.length; i++) {
    for (let j = i + 1; j < dimensionScores.length; j++) {
      const a = dimensionScores[i];
      const b = dimensionScores[j];
      const gap = Math.abs(a.score - b.score);
      if (gap >= 3 && !alerts.some(al => 
        (al.highDimension === a.dimensionId && al.lowDimension === b.dimensionId) ||
        (al.highDimension === b.dimensionId && al.lowDimension === a.dimensionId)
      )) {
        const [high, low] = a.score > b.score ? [a, b] : [b, a];
        alerts.push({
          highDimension: high.dimensionId,
          highScore: high.score,
          lowDimension: low.dimensionId,
          lowScore: low.score,
          gap,
          message: `Desequilibrio severo entre ${high.name} (${high.score}) y ${low.name} (${low.score}). Se recomienda priorizar la inversión en ${low.name}.`,
        });
      }
    }
  }

  return alerts.sort((a, b) => b.gap - a.gap);
}

// Fallback: Calculate result from answers (completion-based) — used only when AI evaluation is not available
export function calculateEnterpriseResult(
  answers: Record<string, EnterpriseDiagnosticAnswer>
): EnterpriseDiagnosticResult {
  const dimensionScores: DimensionScore[] = dimensions.map(dim => {
    let totalQ = 0;
    let answeredQ = 0;
    for (const sub of dim.subSections) {
      for (const q of sub.questions) {
        totalQ++;
        const ans = answers[q.id];
        if (ans && ans.text.trim().length > 0 && !ans.cannotAnswer) {
          answeredQ++;
        }
      }
    }
    // Score based on completion ratio mapped to 1-5 (fallback only)
    const ratio = totalQ > 0 ? answeredQ / totalQ : 0;
    let score: number;
    if (ratio >= 0.9) score = 5;
    else if (ratio >= 0.7) score = 4;
    else if (ratio >= 0.5) score = 3;
    else if (ratio >= 0.25) score = 2;
    else score = 1;

    return {
      dimensionId: dim.id as DimensionId,
      name: dim.fullName,
      score,
      answeredQuestions: answeredQ,
      totalQuestions: totalQ,
    };
  });

  const totalAnswered = dimensionScores.reduce((a, d) => a + d.answeredQuestions, 0);
  const totalQuestions = dimensionScores.reduce((a, d) => a + d.totalQuestions, 0);
  const completionPercentage = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  // Overall level = floor of average scores
  const avgScore = dimensionScores.reduce((a, d) => a + d.score, 0) / dimensionScores.length;
  const overallLevel = Math.max(1, Math.min(5, Math.round(avgScore)));

  const imbalanceAlerts = detectImbalances(dimensionScores);

  return {
    overallLevel,
    overallLevelName: maturityLevelNames[overallLevel].name,
    dimensionScores,
    totalAnswered,
    totalQuestions,
    completionPercentage,
    imbalanceAlerts,
  };
}

// Build result from AI-evaluated scores
export function buildResultFromAIScores(
  answers: Record<string, EnterpriseDiagnosticAnswer>,
  aiScores: { dimensionId: DimensionId; name: string; score: number; subDimensionScores: SubDimensionScore[] }[]
): EnterpriseDiagnosticResult {
  const dimensionScores: DimensionScore[] = aiScores.map(ai => {
    const dim = dimensions.find(d => d.id === ai.dimensionId)!;
    let totalQ = 0;
    let answeredQ = 0;
    for (const sub of dim.subSections) {
      for (const q of sub.questions) {
        totalQ++;
        const ans = answers[q.id];
        if (ans && (ans.text.trim().length > 0 || ans.cannotAnswer)) answeredQ++;
      }
    }
    return {
      dimensionId: ai.dimensionId,
      name: ai.name,
      score: ai.score,
      answeredQuestions: answeredQ,
      totalQuestions: totalQ,
      subDimensionScores: ai.subDimensionScores,
    };
  });

  const totalAnswered = dimensionScores.reduce((a, d) => a + d.answeredQuestions, 0);
  const totalQuestions = dimensionScores.reduce((a, d) => a + d.totalQuestions, 0);
  const completionPercentage = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  const avgScore = dimensionScores.reduce((a, d) => a + d.score, 0) / dimensionScores.length;
  const overallLevel = Math.max(1, Math.min(5, Math.round(avgScore)));

  const imbalanceAlerts = detectImbalances(dimensionScores);

  return {
    overallLevel,
    overallLevelName: maturityLevelNames[overallLevel].name,
    dimensionScores,
    totalAnswered,
    totalQuestions,
    completionPercentage,
    imbalanceAlerts,
  };
}
